import { createAsyncThunk } from '@reduxjs/toolkit'
import { APIError, authenticatedRequest, getExceptionPayload } from '../axiosConfiguration'
import {
  QualityCertificationData,
  QualityCertificationRequest,
  QualityCertificationFilter,
  QualityCertificationDocuments,
} from '../type/qualityCertification'
import * as layoutActions from './layout'
import { create } from 'domain'
import format from 'date-fns/format'

export const getQualityCertificationList =
  createAsyncThunk<QualityCertificationData, QualityCertificationRequest, {
    rejectValue: APIError
  }>(
    '/qualitycertificate',
    async (query, { dispatch, rejectWithValue }) => {
      dispatch(layoutActions.startLoading())

      try {
        const instance = await authenticatedRequest()
        const queryParams = new URLSearchParams()
        const currentLanguage = localStorage.getItem('selectedLanguage') ?? 'en'
        queryParams.append('language_code', currentLanguage)
        if (query.filter.companyCode) {
          queryParams.append('company_code', query.filter.companyCode.toString())
        }
        if (query.filter.customerCode) {
          queryParams.append('customer_code', query.filter.customerCode.toString())
        }
        if (query.filter.certificateDateFrom) {
          queryParams.append('from_date', format(query.filter.certificateDateFrom as Date, 'yyyy-MM-dd'))
        }

        if (query.filter.certificateDateTo) {
          queryParams.append('to_date', format(query.filter.certificateDateTo as Date, 'yyyy-MM-dd'))
        }

        const res = await instance.get<QualityCertificationData>(`/qualitycertificate?${queryParams.toString()}`)

        const processedData = res.data.data.map(item => {
          const processNumbers = (numbers: any) => {
            if (numbers && !Array.isArray(numbers) && typeof numbers === 'object') {
              return Object.entries(numbers)
                .filter(([key, value]) => key !== 'metadata' && value !== null && typeof value === 'string')
                .map(([_, value]) => value as string)
            }
            return numbers
          }

          return {
            ...item,
            coilNumbers: processNumbers(item.coilNumbers),
            lotNumbers: processNumbers(item.lotNumbers),
          }
        })

        dispatch(layoutActions.endLoading())

        return {
          ...res.data,
          data: processedData,
        }
      } catch (error) {
        const err = getExceptionPayload(error, 'qualityCertification:error.getQualityCertificationList')
        console.log(err)
        dispatch(layoutActions.endLoading())
        dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

        return rejectWithValue(err)
      }
    },
  )

export const getQualityCertificationDocument =
  createAsyncThunk<QualityCertificationDocuments, QualityCertificationFilter, {
    rejectValue: APIError
  }>(
    '/qualitycertificate/get-document',
    async (query, { dispatch, rejectWithValue }) => {
      dispatch(layoutActions.startLoading())

      try {
        const instance = await authenticatedRequest()
        const queryParams = new URLSearchParams()
        if (query.companyCode) {
          queryParams.append('company_code', query.companyCode.toString())
        }
        if (query.customerCode) {
          queryParams.append('customer_code', query.customerCode.toString())
        }
        queryParams.append('certificate_number', query.creatorReferenceId.toString())
        const res = await instance.get<QualityCertificationDocuments>(`/qualitycertificate/get-document?${queryParams.toString()}`) //, requestPayload)

        dispatch(layoutActions.endLoading())

        return res.data
      } catch (error) {
        const err = getExceptionPayload(error, `qualityCertification:error.getQualityCertificationDocument`, { creatorReferenceId: query.creatorReferenceId })
        console.log(err)
        dispatch(layoutActions.endLoading())
        dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

        return rejectWithValue(err)
      }
    },
  )